<!-- Responsibility: Hub overview and primary navigation.
Scope: Owns hub-level readiness checks and primary routing triggers. -->
<hub-view class="menu-section active menu-overlay">
  <div class="hub-bg-ring" aria-hidden="true">
    <div class="hub-bg-img" style="background-image: url('/assets/onboarding/img1.png');"></div>
    <div class="hub-bg-img" style="background-image: url('/assets/onboarding/img2.png');"></div>
    <div class="hub-bg-img" style="background-image: url('/assets/onboarding/img3.png');"></div>
    <div class="hub-bg-img" style="background-image: url('/assets/onboarding/img4.png');"></div>
  </div>

  <section class="stage">
    <article data-state={ heroConfig().state }>
      <header>
        <p>{ heroConfig().kicker }</p>
        <h2>{ heroConfig().title }</h2>
      </header>

      <p>{ heroConfig().body }</p>

      <ul aria-label="Hub readiness">
        <li>{ state.hasSaves ? 'Scenario archive online' : 'No scenario archive yet' }</li>
        <li>{ state.hasWorlds ? 'World registry stocked' : 'Fresh world path ready' }</li>
        <li>{ state.hasVessels ? 'Vessel forge prepared' : 'Vessel forge pending' }</li>
      </ul>

      <nav aria-label="Primary hub actions" onclick={ handleHeroAction }>
        <button type="button" data-tip={ heroConfig().ctaTip } data-action="primary">{ heroConfig().cta }</button>
        <button if={ heroConfig().secondaryCta } type="button" data-tip={ heroConfig().secondaryTip } data-action="secondary">{ heroConfig().secondaryCta }</button>
      </nav>
    </article>

    <aside>
      <header>
        <p>SIDE DOCK</p>
        <h3>Keep utility routes nearby, not in the launch line.</h3>
      </header>

      <p>Marketplace, import, and guidance stay visible without competing with the main return or launch path.</p>

      <nav aria-label="Utility hub actions" onclick={ handleUtilityAction }>
        <button type="button" data-tip="Browse published worlds, vessels, and scenarios from the marketplace." data-action="marketplace">BROWSE MARKETPLACE <span>◇</span></button>
        <button type="button" data-tip={ state.hasSaves ? 'Read the quick guide and controls reference.' : 'Start with the quick guide before forging your first run.' } data-entry={ !state.hasSaves ? 'true' : 'false' } data-action="howtoplay">{ !state.hasSaves ? 'START HERE · HOW TO PLAY' : 'HOW TO PLAY' }</button>
      </nav>
    </aside>
  </section>

  <section class="lanes" aria-label="Hub lanes" onclick={ handleLaneAction }>
    <button
      type="button"
      aria-label="Resume game"
        data-tip="Return to the save archive and resume an existing live scenario."
      data-action="resume"
      disabled={ !state.hasSaves }
    >
      <header>
        <p>SCENARIO</p>
        <strong>{ state.hasSaves ? 'READY' : 'EMPTY' }</strong>
      </header>
      <h3>{ state.hasSaves ? 'Resume a live scenario' : 'No saved scenarios yet' }</h3>
      <p>Return to the exact scene state you left, with chronicle and room pressure intact.</p>
      <ul>
        <li>{ state.hasSaves ? 'Fastest return path' : 'Unlocks after first run' }</li>
        <li>Restore-safe</li>
      </ul>
    </button>

    <button type="button" aria-label="World gallery" data-tip="Browse saved worlds or forge a new reality." data-action="worlds">
      <header>
        <p>WORLD</p>
        <strong>{ state.hasWorlds ? 'CURATED' : 'STARTER' }</strong>
      </header>
      <h3>Browse or forge worlds</h3>
      <p>Reuse a known reality or branch into a fresh forge before you commit to a launch path.</p>
      <ul>
        <li>{ state.hasWorlds ? 'Reusable realities waiting' : 'Best first stop' }</li>
        <li>Gallery + forge</li>
      </ul>
    </button>

    <button type="button" aria-label="Vessel gallery" data-tip="Choose or prepare a vessel identity for the next run." data-action="vessels">
      <header>
        <p>VESSEL</p>
        <strong>{ state.hasVessels ? 'READY' : 'MISSING' }</strong>
      </header>
      <h3>Choose a vessel identity</h3>
      <p>Carry a prepared identity into the chosen world, or open vessel creation once the world is set.</p>
      <ul>
        <li>{ state.hasVessels ? 'Identity gallery available' : 'Needs a world first' }</li>
        <li>Profiles + lineage</li>
      </ul>
    </button>

    <button type="button" aria-label="Start new game" data-tip="Review the selected world and vessel together before launch." data-ready={ state.hasReadyPair ? 'true' : 'false' } data-action="start">
      <header>
        <p>PAIRING</p>
        <strong>{ state.hasReadyPair ? 'LAUNCH READY' : 'WAITING' }</strong>
      </header>
      <h3>Review the final pairing</h3>
      <p>Confirm the chosen world and vessel together so the start feels deliberate, not incidental.</p>
      <ul>
        <li>{ state.hasReadyPair ? 'World + vessel aligned' : 'Unlocks when both picks exist' }</li>
        <li>Launch review</li>
      </ul>
    </button>
  </section>

  <script>
    import { dispatcher, navigateState } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import { LOBBY_SELECTION_KEY } from '../lobby/lobby-state.ts'

    export default {
      state: {
        hasSaves: false,
        hasWorlds: false,
        hasVessels: false,
        hasReadyPair: false,
      },

      onMounted() {
        this.refreshStatus()
      },

      async refreshStatus() {
        const [saves, worlds, vessels] = await Promise.all([
          persistenceService.listSaves(),
          persistenceService.listWorlds(),
          persistenceService.listVessels(),
        ])

        let hasReadyPair = false
        try {
          const raw = sessionStorage.getItem(LOBBY_SELECTION_KEY)
          if (raw) {
            const parsed = JSON.parse(raw)
            const worldId = String(parsed?.selectedWorldSaveId || '').trim()
            const vesselKey = String(parsed?.selectedVesselKey || '').trim()
            hasReadyPair = (!!worldId || !!gameStore.importedWorldData) && (!!vesselKey || !!gameStore.importedPlayerData)
          }
        } catch {}

        this.update({
          hasSaves: saves.length > 0,
          hasWorlds: worlds.length > 0,
          hasVessels: vessels.length > 0,
          hasReadyPair
        })
      },

      handleHeroAction(e) {
        const btn = e.target.closest('button[data-action]')
        if (!btn) return
        const action = btn.getAttribute('data-action')
        const config = this.heroConfig()
        if (action === 'primary') config.action?.()
        if (action === 'secondary') config.secondaryAction?.()
      },

      handleUtilityAction(e) {
        const btn = e.target.closest('button[data-action]')
        if (!btn) return
        const action = btn.getAttribute('data-action')
        if (btn.disabled) return
        if (action === 'marketplace') navigateState('marketplace')
        if (action === 'howtoplay') dispatcher.trigger('hub-view', TOPICS.ui.dialogOpen, { content: 'how-to-play-dialog', data: {}, options: { variant: 'notebook' } })
      },

      handleLaneAction(e) {
        const btn = e.target.closest('button[data-action]')
        if (!btn) return
        const action = btn.getAttribute('data-action')
        if (action === 'resume') navigateState('saves')
        if (action === 'worlds') navigateState('worlds')
        if (action === 'vessels') navigateState('vessels')
        if (action === 'start') navigateState('pairing')
      },

      heroConfig() {
        const { hasReadyPair, hasSaves } = this.state

        if (hasReadyPair) {
          return {
            state: 'launch',
            kicker: 'READY GATE',
            title: 'Step straight into the prepared run.',
            body: 'Your world and vessel are already aligned. Use the command gate below to launch immediately, then adjust the pairing only if you want to refine the starting feel.',
            cta: 'LAUNCH PREPARED PAIRING',
          ctaTip: 'Re-enter the last reality that was still in motion.',
            action: () => navigateState('pairing'),
            secondaryCta: 'OR FORGE A DIFFERENT WORLD',
            secondaryTip: 'Open the world gallery to forge or review a reality.',
            secondaryAction: () => navigateState('worlds'),
          }
        }

        if (hasSaves) {
          return {
            state: 'resume',
            kicker: 'RETURN PATH',
            title: 'Re-enter the last reality that was still in motion.',
            body: 'The fastest way back into play is to restore the live scenario already carrying your history, room state, and ongoing pressure.',
            cta: 'RESUME LIVE SCENARIO',
          ctaTip: 'Re-enter the last reality that was still in motion.',
            action: () => navigateState('saves'),
            secondaryCta: 'OR FORGE A DIFFERENT WORLD',
            secondaryTip: 'Open the world gallery to forge or review a reality.',
            secondaryAction: () => navigateState('worlds'),
          }
        }

        return {
          state: 'forge',
          kicker: 'FIRST THRESHOLD',
          title: 'Open a world first, then let the rest of the run gather around it.',
          body: 'Start by choosing or forging the reality itself. Once the world exists, vessel prep and launch review stop feeling like admin and start feeling like intention.',
          cta: 'FORGE YOUR FIRST WORLD',
          ctaTip: 'Open the world gallery and start the first world forge.',
          action: () => navigateState('worlds'),
          secondaryCta: 'READ THE QUICK GUIDE',
          secondaryTip: 'Open the how-to-play guide and controls reference.',
          secondaryAction: () => dispatcher.trigger('hub-view', TOPICS.ui.dialogOpen, { content: 'how-to-play-dialog', data: {}, options: { variant: 'notebook' } }),
        }
      },
    }
  </script>

  <style>
    hub-view {
      position: relative;
      display: block;
      width: 100%;
      max-width: min(100%, 76rem);
      isolation: isolate;
    }

    .hub-bg-ring {
      z-index: 0;
    }

    .stage {
      position: relative;
      z-index: 1;
      display: grid;
      grid-template-columns: minmax(0, 1.9fr) minmax(18rem, 0.92fr);
      gap: var(--space-md);
      margin-bottom: var(--space-sm, var(--space-sm));
      align-items: stretch;
    }

    .stage > article,
    .stage > aside,
    .lanes > button {
      position: relative;
      overflow: clip;
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-ghost), transparent);
      border-radius: var(--radius-lg);
      box-shadow:
        0 1.375rem 2.375rem rgba(0, 0, 0, 0.2),
        0 0 0 1px rgba(255, 255, 255, 0.03),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
    }

    .stage > article {
      display: grid;
      gap: var(--space-md);
      padding: clamp(var(--space-md), 2vw, var(--space-lg));
      min-height: 19rem;
      align-content: end;
      background:
        radial-gradient(circle at 16% 16%, color-mix(in srgb, var(--gold) 12%, transparent), transparent 16%),
        radial-gradient(circle at 12% 24%, color-mix(in srgb, var(--brand-primary) 12%, transparent), transparent 24%),
        linear-gradient(145deg, color-mix(in srgb, var(--surface-card-2) 84%, #010308), color-mix(in srgb, #07101a 16%, #010308) 52%, color-mix(in srgb, var(--surface) 84%, #010308));
      animation: menu-hero-rise 0.32s cubic-bezier(.2, .8, .2, 1) both;
      isolation: isolate;
    }

    .stage > article::before,
    .stage > article::after {
      content: '';
      position: absolute;
      pointer-events: none;
    }

    .stage > article::before {
      inset: 12% auto auto 52%;
      width: clamp(10rem, 28vw, 18rem);
      aspect-ratio: 1;
      border-radius: 50%;
      background:
        radial-gradient(circle, color-mix(in srgb, var(--gold) 12%, transparent), transparent 52%),
        radial-gradient(circle at 52% 48%, color-mix(in srgb, var(--brand-primary) 16%, transparent), transparent 48%);
      filter: blur(0.75rem);
      opacity: 0.7;
      animation: hub-orbit 7.5s ease-in-out infinite alternate;
    }

    .stage > article::after {
      inset: auto -12% 1.25rem 16%;
      height: 12%;
      background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--gold) 16%, transparent), transparent);
      opacity: 0;
      animation: hub-sheen 1s ease-out 0.16s both;
    }

    .stage > article > header,
    .stage > aside > header {
      display: grid;
      gap: var(--space-xs);
      position: relative;
      z-index: 1;
    }

    .stage > article > header > p,
    .stage > aside > header > p,
    .lanes > button > header > p,
    .lanes > button > header > strong {
      margin: 0;
      display: inline-flex;
      align-items: center;
      width: fit-content;
      min-height: var(--space-lg);
      padding: 0.16rem 0.56rem;
      border-radius: 62.4375rem;
      font-size: 0.66rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
      background: color-mix(in srgb, rgba(7, 10, 16, 0.9) 84%, transparent);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
    }

    .stage > article > header > p {
      color: var(--gold);
    }

    .stage > aside > header > p {
      color: var(--brand-primary);
    }

    .stage > article h2,
    .stage > aside h3,
    .lanes > button h3 {
      margin: 0;
      color: var(--text-prose);
      line-height: 1.08;
      letter-spacing: 0.02em;
    }

    .stage > article h2 {
      max-width: 13ch;
      font-size: clamp(var(--space-lg), 4.2vw, 3.1rem);
      text-wrap: balance;
    }

    .stage > article > p,
    .stage > aside > p,
    .lanes > button > p {
      margin: 0;
      color: var(--text-prose);
      line-height: 1.55;
      position: relative;
      z-index: 1;
    }

    .stage > article > p {
      font-size: var(--space-md);
      max-width: 56ch;
    }

    .stage > article > ul,
    .lanes > button > ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
      position: relative;
      z-index: 1;
    }

    .stage > article > ul > li,
    .lanes > button > ul > li {
      color: var(--text-muted);
      font-size: var(--space-sm);
      line-height: 1.35;
      padding: var(--space-xs) 0.56rem;
      border-radius: 62.4375rem;
      border: 1px solid color-mix(in srgb, currentColor 16%, transparent);
      background: color-mix(in srgb, rgba(6, 10, 16, 0.92) 84%, transparent);
    }

    .stage > article > nav,
    .stage > aside > nav {
      display: flex;
      flex-wrap: wrap;
      gap: 0.6rem;
      position: relative;
      z-index: 1;
    }

    .stage > article > nav > button,
    .stage > aside > nav > button {
      min-height: 2.8rem;
      padding: var(--space-sm) var(--space-md);
      border: 1px solid color-mix(in srgb, var(--border) 84%, transparent);
      border-radius: 62.4375rem;
      background: color-mix(in srgb, rgba(6, 10, 16, 0.9) 84%, transparent);
      color: var(--text-prose);
      font: inherit;
      font-size: 0.82rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      cursor: pointer;
      transition:
        transform 0.18s ease,
        border-color 0.18s ease,
        box-shadow 0.18s ease;
    }

    .stage > article > nav > button:first-child {
      min-height: 3.35rem;
      padding-inline: 1.15rem;
      font-size: 0.86rem;
      letter-spacing: 0.14em;
      border-color: color-mix(in srgb, var(--gold) 40%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, var(--gold) 16%, transparent);
    }

    .stage > aside {
      display: grid;
      gap: 0.9rem;
      padding: var(--space-md);
      align-content: start;
      background:
        radial-gradient(circle at top right, color-mix(in srgb, var(--brand-primary) 12%, transparent), transparent 12%),
        linear-gradient(162deg, color-mix(in srgb, var(--surface-card-3) 84%, var(--bg)), color-mix(in srgb, var(--surface-card-2) 84%, var(--bg)));
      animation: menu-hero-rise 0.32s cubic-bezier(.2, .8, .2, 1) 0.05s both;
    }

    .stage > aside h3 {
      font-size: 1.16rem;
      max-width: 17ch;
    }

    .stage > aside > p {
      font-size: var(--space-md);
      color: var(--text-dim, #93a8c2);
    }

    .stage > article > nav > button:hover,
    .stage > article > nav > button:focus-visible,
    .stage > aside > nav > button:hover,
    .stage > aside > nav > button:focus-visible {
      outline: none;
      transform: translateY(-1px);
      border-color: color-mix(in srgb, var(--gold) 48%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, var(--gold) 16%, transparent);
    }

    .stage > aside > nav > button:last-child[data-entry='true'] {
      border-color: color-mix(in srgb, var(--gold) 48%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, var(--gold) 16%, transparent), 0 0 1.125rem rgba(251, 191, 36, 0.14);
    }

    .lanes {
      position: relative;
      z-index: 1;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-sm, var(--space-sm));
      perspective: 75rem;
    }

    .lanes > button {
      display: grid;
      gap: var(--space-sm);
      padding: var(--space-md);
      min-width: 0;
      width: 100%;
      text-align: left;
      border: 1px solid color-mix(in srgb, var(--border) 12%, transparent);
      border-radius: var(--radius-lg);
      color: inherit;
      font: inherit;
      cursor: pointer;
      background: var(--surface-card-1);
      transition:
        transform 0.24s cubic-bezier(0.2, 1, 0.35, 1),
        border-color 0.18s ease,
        box-shadow 0.24s ease;
      transform-origin: center bottom;
      isolation: isolate;
    }

    .lanes > button::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      opacity: 0.12;
      transition: opacity 0.24s ease;
    }

    .lanes > button[data-action="resume"]::before {
      background: radial-gradient(circle at top right, var(--sem-success-fg), transparent 70%);
    }
    .lanes > button[data-action="worlds"]::before {
      background: radial-gradient(circle at top right, var(--gold), transparent 70%);
    }
    .lanes > button[data-action="vessels"]::before {
      background: radial-gradient(circle at top right, var(--brand-primary), transparent 70%);
    }
    .lanes > button[data-action="start"]::before {
      background: radial-gradient(circle at top right, var(--gold), var(--brand-primary), transparent 80%);
      opacity: 0.18;
    }

    .lanes > button:hover::before,
    .lanes > button:focus-visible::before {
      opacity: 0.24;
    }

    .lanes > button > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-xs);
      position: relative;
      z-index: 1;
    }

    .lanes > button > header > p {
      color: var(--text-dim, #93a8c2);
    }

    .lanes > button > header > strong {
      font-weight: 600;
      color: var(--gold);
    }

    .lanes > button:nth-child(2) > header > strong {
      color: var(--sem-success-fg);
    }

    .lanes > button:nth-child(3) > header > strong {
      color: var(--brand-primary);
    }

    .lanes > button h3 {
      font-size: var(--space-md);
      max-width: 18ch;
      position: relative;
      z-index: 1;
    }

    .lanes > button > p {
      font-size: 0.94rem;
      max-width: 35ch;
    }

    .lanes > button:hover,
    .lanes > button:focus-visible {
      outline: none;
      transform: translateY(-0.28rem) scale(1.01);
      box-shadow:
        0 1.75rem 2.875rem rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(255, 255, 255, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.12);
    }

    .lanes > button:nth-child(1):hover,
    .lanes > button:nth-child(1):focus-visible,
    .lanes > button:nth-child(2):hover,
    .lanes > button:nth-child(2):focus-visible,
    .lanes > button:nth-child(4):hover,
    .lanes > button:nth-child(4):focus-visible {
      border-color: color-mix(in srgb, var(--gold) 48%, transparent);
    }

    .lanes > button:nth-child(3):hover,
    .lanes > button:nth-child(3):focus-visible {
      border-color: color-mix(in srgb, var(--brand-primary) 48%, transparent);
    }

    .lanes > button:last-child[data-ready='true'] {
      border-color: color-mix(in srgb, var(--gold) 24%, transparent);
      box-shadow:
        0 0 0 1px color-mix(in srgb, var(--gold) 16%, transparent) inset,
        0 1.375rem 2.375rem rgba(0, 0, 0, 0.2),
        0 0 1.875rem color-mix(in srgb, var(--gold) 12%, transparent);
    }

    .lanes > button:disabled {
      opacity: 0.62;
      cursor: default;
    }

    @media (max-width: 47.5rem) {
      .stage {
        grid-template-columns: 1fr;
      }

      .stage > article {
        min-height: 16rem;
      }

      .lanes {
        grid-template-columns: 1fr;
      }

      .lanes > button {
        grid-column: auto;
      }
    }
  </style>
</hub-view>
